import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/common/PageTitle/PageTitle';
import PageSubtitle from 'components/common/PageSubtitle/PageSubtitle';
import InfoMessage from 'components/common/InfoMessage/InfoMessage';
import Button from 'components/common/Button/Button';
import Download from 'components/assets/icons/Download';
import Eye from 'components/assets/icons/Eye';
import Card from 'components/common/Card/Card';
import Link from 'components/common/Link/Link';
import style from './ContractDocuments.module.scss';
import { getContractDocuments } from 'helpers/contract';
import { downloadDocument, downloadDocumentAPI } from 'helpers/files';
import { useLocation, useParams } from 'react-router-dom';
import api from 'helpers/api';
import ExclamationTriangle from 'components/assets/icons/ExclamationTriangle.js';

const ContractDocuments = () => {
  const { t, i18n: { language } } = useTranslation();
  const params = useParams();
  const { state } = useLocation();

  const [contract, setContract] = useState(state?.contract ?? null);
  const [documents, setDocuments] = useState(state?.documents ?? []);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  const [isStatusRefused, setIsStatusRefused] = useState(false);
  const [refusedComment, setRefusedComment] = useState('');
  const [refusedDocumentName, setRefusedDocumentName] = useState('');

  const fileInputRefs = useRef([]);

  const normalize = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

  // legalDocuments peut être un objet OU un tableau, on normalise
  const getLDObj = (ld) => {
    if (!ld) return null;
    if (Array.isArray(ld)) return ld.length > 0 ? ld[0] : null;
    return ld;
  };

  // ✅ Dans ton cas, NI/IPID/FIP sont des strings (URL ou "-")
  // On considère "actif" si la valeur est une string non vide et différente de "-"
  const isDocAvailable = (obj, key) => {
    const v = obj?.[key];

    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) return false;
      if (s === '-') return false;
      return true; // URL ou valeur non vide
    }

    // fallback si ça devient bool/number
    return v === true || v === 1 || v === 'true' || v === '1';
  };

  const ldKeys = ['ni', 'ipid', 'fip'];

  useEffect(() => {
    const getContract = async () => {
      try {
        const adhesionRes = await api.get({ url: `/adhesions/${params.id}/client` });
        const adh = adhesionRes.adhesion;

        // ✅ Logs DATA reçues
        console.log('[ContractDocuments] adhesion id:', adh?._id);
        console.log('[ContractDocuments] rateType.legalDocuments raw:', adh?.rateType?.legalDocuments);
        console.log('[ContractDocuments] contractType.legalDocuments raw:', adh?.contractType?.legalDocuments);
        console.log('[ContractDocuments] legalDocumentClients raw:', adh?.contractType?.legalDocuments?.legalDocumentClients);
        console.log('[ContractDocuments] clientDocuments snapshot:', (adh?.clientDocuments || []).map(d => ({
          _id: d._id,
          type: d.type,
          name: d.name,
          originalName: d.originalName,
          status: d.status,
          LegalDocumentClients: d.LegalDocumentClients?._id,
        })));

        setContract(adh);
        setDocuments(await getContractDocuments(adh));

        // Rechercher le dernier document avec le statut "refusé"
        const refusedDocument = adh.clientDocuments?.find(doc => doc.status === 'refusé');

        if (refusedDocument) {
          setIsStatusRefused(true);

          const lastComment =
            refusedDocument.commentsDocuments?.[refusedDocument.commentsDocuments.length - 1];
          setRefusedComment(lastComment ? lastComment.commentsDashboard : t('contract:documents.unspecifiedReason'));

          const associatedLegalDocument = adh.contractType?.legalDocuments?.legalDocumentClients?.find(
            legalDoc => String(legalDoc._id) === String(refusedDocument?.LegalDocumentClients?._id)
          );

          if (associatedLegalDocument) {
            setRefusedDocumentName(associatedLegalDocument.dashboardName);
          } else {
            setRefusedDocumentName(refusedDocument?.type?.toUpperCase?.() || t('contract:documents.fallbackName'));
          }
        } else {
          setIsStatusRefused(false);
          setRefusedComment('');
          setRefusedDocumentName('');
        }
      } catch (error) {
        console.log('Erreur lors de la récupération du contrat', error);
      }
    };

    getContract();
  }, [params.id, language]);

  const handleUploadClick = (index) => {
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].click();
    }
  };

  const handleFileChange = async (event, documentClient, index) => {
    const file = event.target.files[0];
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

    if (file && !allowedTypes.includes(file.type)) {
      setErrorMessage(t('contract:documents.invalidFormat'));
      return;
    }

    setErrorMessage('');
    setUploading(true);

    const formData = new FormData();
    formData.append('files', file);

    // ✅ Si tu utilises encore l'upload de "pièces" (legalDocumentClients)
    // Ici, NI/IPID/FIP dans rateType sont des URLs => pas d'upload associé
    // Donc on upload uniquement si c'est une pièce.
    formData.append('legalDocumentClientId', documentClient._id);

    try {
      await api.post({
        url: `/adhesions/${params.id}/legaldocuments`,
        contentType: 'multipart/form-data',
        data: formData,
      });

      setSuccessMessage(t('contract:documents.uploadSuccess'));

      const adhesionRes = await api.get({ url: `/adhesions/${params.id}/client` });
      const adh = adhesionRes.adhesion;

      setContract(adh);
      setDocuments(await getContractDocuments(adh));

      const stillRefusedDocument = adh.clientDocuments?.some(doc => doc.status === 'refusé');
      setIsStatusRefused(stillRefusedDocument);

      setUploading(false);
    } catch (error) {
      setErrorMessage(t('contract:documents.uploadError'));
      setUploading(false);
      console.log("Erreur lors de l'upload:", error);
    }
  };

  const missingDocumentsList = contract?.legalDocumentsInfo
    ?.filter(doc => doc.status === "Manquant")
    ?.map(doc => (doc.name || '').toLowerCase())
    ?.join(" et votre ");

  const AcceptedDoc = ({ documentId, name }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div onClick={() => downloadDocumentAPI({ documentId, name })}>
        <Download />
      </div>
      <Link
        className={style.download}
        label={<Eye />}
        to={`${process.env.REACT_APP_API_URL}/dashboard/documents/${documentId}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginLeft: '8px' }}
      />
    </div>
  );

  const RefusedDoc = ({ documentClient, index }) => (
    <>
      <div
        onClick={() => handleUploadClick(index)}
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
      >
        <Download style={{ marginRight: '8px' }} />
        <span style={{ color: '#2322FE' }}>
          {uploading ? t('common:uploading') : t('common:upload')}
        </span>
      </div>
      <input
        ref={el => (fileInputRefs.current[index] = el)}
        type="file"
        style={{ display: 'none' }}
        accept=".pdf,.jpeg,.jpg,.png"
        onChange={(e) => handleFileChange(e, documentClient, index)}
      />
    </>
  );

  const CheckingDoc = ({ status }) => (
    <Button
      className={`${style['claim-button']} ${style['custom-claim-button']}`}
      label={status}
      onClick={() => setErrorMessage('')}
      style={{ backgroundColor: '#E68D3E', color: '#FFFFFF' }}
    />
  );

  const UploadDoc = ({ index }) => (
    <div
      onClick={() => handleUploadClick(index)}
      style={{ display: 'flex', alignItems: 'center' }}
    >
      <Download style={{ marginRight: '8px' }} />
      <span style={{ color: '#2322FE' }}>
        {uploading ? t('common:uploading') : t('common:upload')}
      </span>
    </div>
  );

  // ✅ Construire la liste à afficher selon ta règle (rateType prioritaire)
  const rateTypeLD = getLDObj(contract?.rateType?.legalDocuments);
  const contractTypeLD = getLDObj(contract?.contractType?.legalDocuments);

  const rateTypeRequired = rateTypeLD ? ldKeys.filter(k => isDocAvailable(rateTypeLD, k)) : [];
  const contractTypeRequired = contractTypeLD ? ldKeys.filter(k => isDocAvailable(contractTypeLD, k)) : [];

  const requiredLD = rateTypeRequired.length > 0 ? rateTypeRequired : contractTypeRequired;

  // ✅ Logs CALCUL
  console.log('[ContractDocuments] rateTypeLD normalized:', rateTypeLD);
  console.log('[ContractDocuments] contractTypeLD normalized:', contractTypeLD);
  console.log('[ContractDocuments] rateTypeRequired:', rateTypeRequired);
  console.log('[ContractDocuments] contractTypeRequired:', contractTypeRequired);
  console.log('[ContractDocuments] requiredLD:', requiredLD);

  // docs NI/IPID/FIP à rendre (docs informatifs: URL)
  const docsToRender = requiredLD.map(k => ({
    _id: k,
    dashboardName: k.toUpperCase(),
    __kind: 'LD',
  }));

  // pièces (legalDocumentClients) uniquement si aucun NI/IPID/FIP requis
  /*const piecesToRender =
    requiredLD.length === 0
      ? (contract?.contractType?.legalDocuments?.legalDocumentClients || []).map(d => ({ ...d, __kind: 'PIECE' }))
      : [];*/

    const piecesToRender =
    (contract?.contractType?.legalDocuments?.legalDocumentClients || [])
        .filter(d => d?.active !== false); // optionnel

  console.log('[ContractDocuments] docsToRender:', docsToRender);
  console.log('[ContractDocuments] piecesToRender length:', piecesToRender.length);

  // URL doc (rateType ou contractType)
  const getDocUrl = (key) => {
    const v = rateTypeLD?.[key] ?? contractTypeLD?.[key];
    if (typeof v !== 'string') return null;
    const s = v.trim();
    return s && s !== '-' ? s : null;
  };

  return (
    contract && (
      <>
        <PageTitle text={contract.contractType?.dashboardName || ''} />
        <PageSubtitle text={`${t('contract:item.partnerDescription')} ${contract.partner?.commercialName || ''}`} />

        {isStatusRefused && (
          <InfoMessage
            className={style['info-message']}
            type={'error'}
            prefix={<ExclamationTriangle />}
            label={
              <>
                {t('contract:documents.refusedTitle', { name: refusedDocumentName })}
                <br />
                {t('contract:documents.refusedReason', { reason: refusedComment })}
              </>
            }
          />
        )}

        {errorMessage && (
          <InfoMessage
            className={style['info-message']}
            label={errorMessage}
            prefix={<ExclamationTriangle styles={{ fill: '#B94402' }} />}
            type={'error'}
            suffix={
              <Button
                className={`${style['claim-button']} ${style['custom-claim-button']}`}
                label={t('contract:documents.retry')}
                onClick={() => setErrorMessage('')}
                type="primary"
              />
            }
          />
        )}

        {successMessage && (
          <InfoMessage
            className={style['info-message']}
            label={successMessage}
            prefix={<ExclamationTriangle styles={{ fill: '#58B441' }} />}
            type={'success'}
            suffix={
              <Button
                className={`${style['claim-button']} ${style['custom-claim-button']}`}
                label={t('contract:documents.ok')}
                onClick={() => setSuccessMessage('')}
                type="primary"
              />
            }
          />
        )}

        {missingDocumentsList && (
          <InfoMessage
            className={style['info-message']}
            label={t('contract:documents.uploadRequest', { list: missingDocumentsList })}
            prefix={<ExclamationTriangle styles={{ fill: '#E78D3D' }} />}
            type={'warning'}
          />
        )}

        <div className={style.list}>
          {/* ✅ NI / IPID / FIP (docs informatifs via URL) */}
          {docsToRender.map((docItem) => {
            const url = getDocUrl(docItem._id);

            // log par doc
            console.log('[ContractDocuments] render LD', docItem._id, 'url:', url);

            return (
              <Card
                className={style['list-item']}
                key={`ld-${docItem._id}`}
                title={<span className={style.bold}>{docItem.dashboardName}</span>}
                warning={false}
                hiddenContent={true}
                cornerContent={
                  url ? (
                    <>
                      <div onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                        <Eye />
                      </div>
                    </>
                  ) : null
                }
              />
            );
          })}

          {/* ✅ Pièces demandées au client (legalDocumentClients) seulement si aucun NI/IPID/FIP */}
          {piecesToRender.map((documentClient, index) => {
            const existingDocument = contract.clientDocuments?.find(doc =>
              doc.LegalDocumentClients && String(doc.LegalDocumentClients._id) === String(documentClient._id)
            );

            const isStatusAccepted = existingDocument && existingDocument.status === 'accepté';
            const isStatusRefusedLocal = existingDocument && existingDocument.status === 'refusé';

            return (
              <Card
                className={style['list-item']}
                key={`piece-${documentClient._id}`}
                title={<span className={style.bold}>{documentClient.dashboardName || t('contract:document.legalDocumentClient')}</span>}
                warning={!existingDocument}
                error={isStatusRefusedLocal}
                content={!existingDocument && (
                  <input
                    ref={el => (fileInputRefs.current[index] = el)}
                    type="file"
                    accept=".pdf,.jpeg,.jpg,.png"
                    onChange={e => handleFileChange(e, documentClient, index)}
                    hidden
                  />
                )}
                hiddenContent={true}
                cornerContent={
                  <>
                    {existingDocument
                      ? isStatusAccepted
                        ? <AcceptedDoc documentId={existingDocument._id} name={existingDocument.originalName || existingDocument.name} />
                        : isStatusRefusedLocal
                          ? <RefusedDoc documentClient={documentClient} index={index} />
                          : <CheckingDoc status={existingDocument.status} />
                      : <UploadDoc index={index} />
                    }
                  </>
                }
              />
            );
          })}

          {/* Autres documents (liste "fichiers") */}
          {documents.map((item, index) => (
            <Card
              className={style['list-item']}
              key={`contract-document-${index}`}
              title={<span className={style.bold}>{t(`contract:document.${item.type}`)}</span>}
              cornerContent={
                <>
                  <div onClick={() => downloadDocument({ contract, type: item.type })}>
                    <Download />
                  </div>
                  <Link
                    className={style.download}
                    label={<Eye />}
                    to={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                </>
              }
            />
          ))}
        </div>
      </>
    )
  );
};

export default ContractDocuments;
